// The Pricing Index shows ONE signed "Price Adjust" per row, but the API still
// stores the split `above` / `back` pair that Deal.get_cost() consumes as
// `cost = base - back + above`. This module is the only place that translates.

import {costFromInput, deriveFromCost, hasBase, round2, toAmount} from "../../components/costCalculatorMath";

// Currency inputs return strings, the API returns numbers or decimal strings,
// and blank means zero — normalize before comparing or doing arithmetic.
export const toMoneyNumber = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

export const getPriceAdjust = (above, back) => toMoneyNumber(above) - toMoneyNumber(back)

// Positive goes entirely on `above`, negative entirely on `back`, normalizing
// rows that carried both. `|| 0` collapses the -0 that '-' over an empty box
// multiplies out to.
export const getAboveBackFromPriceAdjust = (priceAdjust) => {
    const adjust = toMoneyNumber(priceAdjust) || 0

    return adjust < 0 ? {above: 0, back: -adjust} : {above: adjust, back: 0}
}

// Control state: `sign` is 1, -1 or null (neutral), `magnitude` is the unsigned
// box contents. Zero is neutral with a BLANK box rather than a literal 0 —
// react-number-format only strips leading zeros on blur, so a box showing '0'
// turns a typed 1200 into '01,200'.
export const getPriceAdjustState = (above, back) => {
    const adjust = getPriceAdjust(above, back)

    if (adjust === 0) return {sign: null, magnitude: ''}

    return {sign: adjust < 0 ? -1 : 1, magnitude: Math.abs(adjust)}
}

// `?? 1` only ever applies to a zero magnitude; it stops callers multiplying by null.
export const getSignedPriceAdjust = ({sign, magnitude}) =>
    (sign ?? 1) * Math.abs(toMoneyNumber(magnitude))

// Typing establishes direction, so the user never has to click + first. Once a
// direction exists it is the +/- control's to change: clearing the box is the
// first half of retyping a magnitude, not a decision to switch sides, so a
// -2,500 row retyped as 2,000 stays a discount instead of becoming a markup.
export const getPriceAdjustAfterMagnitude = (priceAdjust, magnitude) => {
    const sign = priceAdjust.sign ?? (toMoneyNumber(magnitude) === 0 ? null : 1)

    return {sign, magnitude}
}

// ── % off MSRP ──
// A THIRD view of the same above/back value, not a second adjustment stacked on
// top of it. The percentage is quoted off MSRP, but the adjustment it produces is
// measured from whatever base Deal.get_cost() will use — so on an invoiced row a
// discount off MSRP can land as a POSITIVE `above`.

// The one place the base is chosen. hasBase, not truthiness, so a 0 / null / ''
// invoice falls back to MSRP exactly the way Python's `invoice or msrp` does.
const getCostBase = (msrp, invoice) => hasBase(invoice) ? toAmount(invoice) : toAmount(msrp)

// Null means "leave above/back alone" (a cleared box, or no MSRP to measure off).
// Zero is different: it is a real, neutral adjustment.
export const getPriceAdjustFromMsrpPercentage = (msrp, invoice, percentage) => {
    // 0% reads as "no percentage input", matching the Cost Calculator's own
    // falsy check — never as "price this row at exactly MSRP".
    if (toAmount(percentage) === 0) return 0

    // invoice is deliberately not passed: the TARGET is quoted off MSRP. Only the
    // base it is measured FROM is invoice-aware, below.
    const targetCost = costFromInput('msrp_percentage', percentage, msrp, null)
    if (targetCost === null) return null

    return round2(targetCost - getCostBase(msrp, invoice))
}

// The inverse, for display only — never fed back into above/back, since the
// rounded percentage would move cost by a few dollars on the round trip.
export const getMsrpPercentage = (msrp, invoice, above, back) => {
    if (!hasBase(msrp)) return null

    const cost = getCostBase(msrp, invoice) - toMoneyNumber(back) + toMoneyNumber(above)

    // Only the MSRP relationship is wanted here, so the invoice half is skipped.
    // Returns null on its own when cost >= msrp — a price at or above MSRP has
    // no "% off" to show.
    return deriveFromCost(msrp, null, cost).msrp_percentage
}
